import { useEffect, useState, type ReactNode } from 'react';
import { Button, Modal, Skeleton } from '@immo/ui-kit';
import { DownloadIcon } from '@/components/layout/icons';
import { apiClient } from '@/lib/auth/api-client';
import { useTranslations } from '@/i18n/use-translations';

const NS = 'pages.admin.properties.detail.memory.viewer';

const PDF_TYPE = 'application/pdf';
const IMAGE_TYPES = ['image/jpeg', 'image/png'];
const NOT_FOUND = 404;

type ViewerState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly url: string; readonly contentType: string }
  | { readonly kind: 'unsupported' }
  | { readonly kind: 'not-found' }
  | { readonly kind: 'failed' };

type MemoryDocumentViewerProps = {
  readonly propertyId: string;
  readonly document: { readonly id: string; readonly name: string } | null;
  readonly onClose: () => void;
};

/**
 * Opens the document a memory entry was read from, so an operator can check the
 * statement against its source. The content type comes from the document list —
 * the entry and the read response carry none — and the URL is minted only while
 * the viewer is open, never when the list renders.
 */
export function MemoryDocumentViewer({ propertyId, document, onClose }: MemoryDocumentViewerProps): ReactNode {
  const t = useTranslations(NS);
  const [state, setState] = useState<ViewerState>({ kind: 'loading' });
  const documentId = document?.id ?? null;

  useEffect(() => {
    if (documentId === null) return;

    let cancelled = false;

    setState({ kind: 'loading' });

    (async () => {
      try {
        const list = await apiClient.adminProperties.listDocuments({ pathParams: { id: propertyId }, query: {} });

        if (cancelled) return;

        if (!list.ok) return setState({ kind: 'failed' });

        const found = list.body.data.find((item) => item.id === documentId);

        if (found === undefined) return setState({ kind: 'not-found' });

        if (found.contentType !== PDF_TYPE && !IMAGE_TYPES.includes(found.contentType)) {
          return setState({ kind: 'unsupported' });
        }

        const minted = await apiClient.adminProperties.readDocument({
          pathParams: { id: propertyId, documentId },
          query: { mode: 'preview' },
        });

        if (cancelled) return;

        if (minted.ok) return setState({ kind: 'loaded', url: minted.body.url, contentType: found.contentType });

        setState({ kind: minted.status === NOT_FOUND ? 'not-found' : 'failed' });
      } catch {
        if (!cancelled) setState({ kind: 'failed' });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [propertyId, documentId]);

  const download = async () => {
    if (documentId === null) return;

    try {
      const minted = await apiClient.adminProperties.readDocument({
        pathParams: { id: propertyId, documentId },
        query: { mode: 'download' },
      });

      if (!minted.ok) return setState({ kind: minted.status === NOT_FOUND ? 'not-found' : 'failed' });

      // No `download` attribute and no blob fetch: the URL is a cross-origin
      // attachment target, so the browser saves it under the stored filename.
      const anchor = window.document.createElement('a');

      anchor.href = minted.body.url;
      anchor.rel = 'noopener';
      anchor.click();
    } catch {
      setState({ kind: 'failed' });
    }
  };

  const name = document?.name ?? '';
  const canDownload = state.kind === 'loaded' || state.kind === 'unsupported' || state.kind === 'failed';

  return (
    <Modal
      open={document !== null}
      onClose={onClose}
      size="xl"
      confirmOnClose={false}
      title={
        <span className="truncate" title={name}>
          {t('title', { name })}
        </span>
      }
      footer={
        canDownload ? (
          <Button type="button" variant="primary" onClick={() => void download()}>
            <span className="inline-flex items-center gap-2">
              <DownloadIcon className="h-4 w-4" aria-hidden />
              {t('download')}
            </span>
          </Button>
        ) : undefined
      }
    >
      <div className="flex min-h-[60vh] items-stretch justify-center">
        <ViewerBody state={state} name={name} />
      </div>
    </Modal>
  );
}

function ViewerBody({ state, name }: { state: ViewerState; name: string }): ReactNode {
  const t = useTranslations(NS);

  if (state.kind === 'loading') {
    return (
      <div role="status" aria-busy="true" aria-label={t('loading')} className="flex flex-1">
        <Skeleton className="min-h-[70vh] w-full border border-border" />
      </div>
    );
  }

  if (state.kind === 'unsupported') return <Notice text={t('no-preview')} />;

  if (state.kind === 'not-found') return <Notice text={t('not-found')} />;

  if (state.kind === 'failed') return <Notice text={t('error')} danger />;

  if (state.contentType === PDF_TYPE) {
    return (
      <iframe
        src={state.url}
        title={name}
        // No `sandbox`: Chrome renders PDFs through an internal viewer extension
        // that a sandboxed frame blocks, leaving a blank preview with no console
        // error. The frame is a cross-origin presigned URL and cannot reach the
        // parent either way.
        className="h-full min-h-[70vh] w-full rounded-md border border-border"
      />
    );
  }

  return <img src={state.url} alt={name} className="max-h-[70vh] max-w-full rounded-md object-contain" />;
}

function Notice({ text, danger = false }: { text: string; danger?: boolean }): ReactNode {
  return (
    <div className="flex flex-1 flex-col items-center justify-center text-center">
      <p className={`text-base-sm ${danger ? 'text-danger-600' : 'text-text-secondary'}`}>{text}</p>
    </div>
  );
}
